import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useAuthStore } from '@/store/auth';
import { API_CONFIG, getApiUrl, getNormalizedApiBaseUrl } from '@/lib/config';
import type {
  PlanPromptPreview,
  PlanPromptPreviewRequest,
} from '@/lib/types';
import type { SharedReportPayload } from '@/components/reports/types';

// Get base URL dynamically at runtime to support window.__ENV__ from config.js
const getBaseUrl = (): string => `${getNormalizedApiBaseUrl()}/api`;

const buildRelativeUrl = (url: string): string => url.replace(/^\/+/, '');

// Create axios instance - baseURL is set dynamically via interceptor
// Note: baseURL includes /api, so all endpoint paths should be relative (no leading slash)
const apiClient: AxiosInstance = axios.create({
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set baseURL dynamically on each request to support runtime config
apiClient.interceptors.request.use((config) => {
  config.baseURL = getBaseUrl();
  return config;
});

// Helper to decode JWT and check expiry
const isTokenExpiringSoon = (token: string, thresholdMinutes: number = 5): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const threshold = thresholdMinutes * 60 * 1000;
    return (exp - now) < threshold;
  } catch {
    return false;
  }
};

// Track ongoing refresh to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

// Shared refresh core — sets guards and returns the promise.
// Callers: await (refreshPromise ?? performTokenRefresh()) to serialize.
const performTokenRefresh = (): Promise<void> => {
  const { tokens } = useAuthStore.getState();

  isRefreshing = true;
  refreshPromise = axios.post(getApiUrl('auth/refresh'), {
    refresh_token: tokens!.refresh_token,
  }).then((response) => {
    useAuthStore.getState().setTokens(response.data);
  }).finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
};

// Proactive token refresh function
export const proactiveTokenRefresh = async (): Promise<void> => {
  const { tokens } = useAuthStore.getState();

  if (!tokens?.access_token || !tokens?.refresh_token) return;

  // Check if token is expiring in the next 5 minutes
  if (isTokenExpiringSoon(tokens.access_token, 5)) {
    try {
      // Queue behind any in-progress refresh; start one if none is running
      await (refreshPromise ?? performTokenRefresh());
      console.log('[Auth] Token proactively refreshed');
    } catch (error: any) {
      console.warn('[Auth] Proactive refresh failed:', error.message);
    }
  }
};

// Visibility-change refresh — replaces useTokenRefresh hook's listener.
// Uses shared isRefreshing/refreshPromise guards to prevent races.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const { tokens, isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated || !tokens?.access_token || !tokens?.refresh_token) return;
    if (isTokenExpiringSoon(tokens.access_token, 15)) {
      (refreshPromise ?? performTokenRefresh()).catch(() => {});
    }
  });
}

// Periodic background refresh for idle tabs — replaces useTokenRefresh interval.
if (typeof window !== 'undefined') {
  setInterval(() => {
    const { tokens, isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated || !tokens?.access_token || !tokens?.refresh_token) return;
    if (isTokenExpiringSoon(tokens.access_token, 15)) {
      (refreshPromise ?? performTokenRefresh()).catch(() => {});
    }
  }, 10 * 60 * 1000);
}

// Request interceptor to add JWT token
// Note: Impersonation is now handled via JWT token itself, no header needed!
apiClient.interceptors.request.use(
  async (config) => {
    // Proactively refresh token before making request (non-blocking for refresh endpoint)
    if (!config.url?.includes('/auth/refresh')) {
      await proactiveTokenRefresh();
    }
    
    const { tokens, currentStoreId, isAuthenticated } = useAuthStore.getState();
    
    // Check if X-Store-ID is already set (case-insensitive)
    const hasStoreIdHeader = 
      config.headers['X-Store-ID'] || 
      config.headers['x-store-id'] ||
      (typeof config.headers.get === 'function' && (config.headers.get('X-Store-ID') || config.headers.get('x-store-id')));

    // Add JWT token (contains impersonation metadata if active)
    if (tokens?.access_token) {
      config.headers.Authorization = `Bearer ${tokens.access_token}`;
    }

    // Add explicit store context header if selected and authenticated
    // Note: Don't overwrite if already set (e.g., from fetchEntityDetails)
    if (isAuthenticated && currentStoreId && !hasStoreIdHeader) {
      config.headers['X-Store-ID'] = currentStoreId.toString();
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh and error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const { tokens, logout, setError } = useAuthStore.getState();
    
    // 409 connector_reauth_required — surface to callers, never log out or refresh
    if (error.response?.status === 409 && (error.response?.data as Record<string, unknown>)?.error_code === 'connector_reauth_required') {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // If already logged out, silently reject — don't flash 'session expired'
      const { isAuthenticated } = useAuthStore.getState();
      if (!isAuthenticated) {
        return Promise.reject(error);
      }

      // Try to refresh token if we have a refresh token
      if (tokens?.refresh_token) {
        try {
          // Queue behind any in-progress refresh; start one if none is running.
          // This ensures concurrent 401s result in exactly ONE refresh call.
          await (refreshPromise ?? performTokenRefresh());

          // Retry with the new token read from the store (not from a stale closure)
          if (error.config) {
            const newAccessToken = useAuthStore.getState().tokens?.access_token;
            error.config.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiClient.request(error.config);
          }
        } catch (refreshError) {
          // Refresh failed, logout user
          logout();
          setError('Session expired. Please login again.');

          // Redirect to login if we're not already there
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
          }
        }
      } else {
        // No refresh token, logout user
        logout();
        setError('Session expired. Please login again.');

        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
        }
      }
    }

    return Promise.reject(error);
  }
);

// API client methods
export const api = {
  // Direct apiClient methods for custom endpoints
  get: (url: string, config?: any) => apiClient.get(buildRelativeUrl(url), config),
  post: (url: string, data?: any, config?: any) => apiClient.post(buildRelativeUrl(url), data, config),
  patch: (url: string, data?: any, config?: any) => apiClient.patch(buildRelativeUrl(url), data, config),
  put: (url: string, data?: any, config?: any) => apiClient.put(buildRelativeUrl(url), data, config),
  delete: (url: string, config?: any) => apiClient.delete(buildRelativeUrl(url), config),

  // Authentication
  auth: {
    register: (data: { username: string; name: string; email: string; password: string; timezone?: string }) =>
      apiClient.post('/auth/register', data),
    
    login: (data: { username: string; password: string }) =>
      apiClient.post('/auth/login', data),
    
    googleLogin: (credential: string, timezone?: string) =>
      apiClient.post('/auth/google/verify', { credential, ...(timezone ? { timezone } : {}) }),

    googleExchange: (code: string) =>
      apiClient.post('/auth/google/exchange', { code }),
    
    refresh: (refresh_token: string) =>
      apiClient.post('/auth/refresh', { refresh_token }),
    
    me: () =>
      apiClient.get('/auth/me'),
    
    updatePassword: (data: { old_password: string; new_password: string }) =>
      apiClient.put('/auth/password', data),

    updateProfile: (data: { name?: string; email?: string; timezone?: string }) =>
      apiClient.put('/auth/profile', data),

    logout: () =>
      apiClient.delete('/auth/logout'),
  },



  // Settings
  settings: {
    get: () =>
      apiClient.get('/settings/user'),
    
    getDefaults: () =>
      apiClient.get('/settings/user/defaults'),
    
    update: (data: any) =>
      apiClient.put('/settings/user', data),
    
    reset: () =>
      apiClient.post('/settings/user/reset'),

    downloadPlugin: (slug: string) =>
      apiClient.get(`/settings/plugins/${encodeURIComponent(slug)}/download`, {
        responseType: 'blob',
      }),

    adminPluginStatus: (slug: string) =>
      apiClient.get(`/v1/plugins/admin/plugins/${encodeURIComponent(slug)}/status`),

    adminPublishPlugin: (slug: string) =>
      apiClient.post(`/v1/plugins/admin/plugins/${encodeURIComponent(slug)}/publish`, {}),

    adminResetAIConfigs: () =>
      apiClient.post('/settings/admin/reset-ai-configs'),
    
    adminCleanDashboardCache: () =>
      apiClient.post('/settings/admin/clean-dashboard-cache'),
    
    adminCleanupOrphans: () =>
      apiClient.post('/settings/admin/cleanup-orphans'),
    
    // Global AI Config V2 (Admin-only) - Per-stage model selection
    getGlobalAIConfigV2: () =>
      apiClient.get('/settings/admin/ai-config-v2'),

    updateGlobalAIConfigV2: (data: {
      simple_model?: string;
      advanced_model?: string;
      temperature?: number;
      max_tokens?: number;
      stage_models?: {
        gatekeeper?: string;
        instant_answer?: string;
        capability?: string;
        planner?: string;
        executor?: string;
        summarizer?: string;
        graphs?: string;
        recommendations?: string;
        translation?: string;
      };
    }) =>
      apiClient.put('/settings/admin/ai-config-v2', data),
    
    getApiConfig: () =>
      apiClient.get('/settings/api-config'),
    
    saveApiConfig: (data: { base_url: string; api_key: string }) =>
      apiClient.put('/settings/api-config', data),
    
    deleteApiConfig: () =>
      apiClient.delete('/settings/api-config'),
    
    testApiConfig: (data: { base_url: string; api_key: string; use_proxy?: boolean; proxy_url?: string }) =>
      apiClient.post('/settings/api-config/test', data),

    syncStore: (data: { base_url?: string; api_key?: string; platform?: string; use_proxy?: boolean; proxy_url?: string }) =>
      apiClient.post('/settings/api-config/sync-store', data),

    // WooCommerce REST write-back credentials
    getWooCommerceRestStatus: () =>
      apiClient.get('/settings/woocommerce-rest/status'),

    saveWooCommerceRest: (data: { consumer_key?: string; consumer_secret?: string }) =>
      apiClient.put('/settings/woocommerce-rest', data),

    testWooCommerceRest: () =>
      apiClient.post('/settings/woocommerce-rest/test'),

    deleteWooCommerceRest: () =>
      apiClient.delete('/settings/woocommerce-rest'),

    // Store-level competitor allowlist. Store-scoped, not user-scoped — the
    // active store comes from the X-Store-ID interceptor above, so no endpoint
    // here takes a store id.
    getCompetitors: () =>
      apiClient.get('/settings/competitors'),

    addCompetitor: (data: { display_name: string; domain: string; enabled?: boolean }) =>
      apiClient.post('/settings/competitors', data),

    // Partial update: omitted fields stay as they are. `enabled: false` DISABLES
    // (row retained, excluded from checks) — it is not deleteCompetitor.
    updateCompetitor: (
      id: number,
      data: { display_name?: string; domain?: string; enabled?: boolean }
    ) => apiClient.patch(`/settings/competitors/${id}`, data),

    deleteCompetitor: (id: number) =>
      apiClient.delete(`/settings/competitors/${id}`),

    getPluginStatus: () =>
      apiClient.get('/settings/api-config/plugin-status'),
    
    getMemoryStatus: () =>
      apiClient.get('/settings/api-config/memory-status'),
    
    getStoreProfile: () =>
      apiClient.get('/settings/store-profile'),
    
    getModels: () =>
      apiClient.get('/settings/models'),

    // questions
    getQuickQuestions: () =>
      apiClient.get('/settings/quick-questions'),
    
    updateQuickQuestions: (data: { questions: Record<string, string[]> }) =>
      apiClient.put('/settings/quick-questions', data),
    
    resetQuickQuestions: () =>
      apiClient.post('/settings/quick-questions/reset'),
    
    // AI questions
    generateAIQuickQuestions: (data?: { 
      categories?: string[]; 
      questions_per_category?: number 
    }) =>
      apiClient.post('/settings/ai-quick-questions/generate', data || {}, {
        timeout: 120000 // 2 minutes - AI generation takes 40-60 seconds
      }),
    
    getAIQuickQuestions: () =>
      apiClient.get('/settings/ai-quick-questions'),
    
    getAIQuickQuestionsStatus: () =>
      apiClient.get('/settings/ai-quick-questions/status'),
    
    toggleAIQuickQuestions: (enabled: boolean) =>
      apiClient.post('/settings/ai-quick-questions/toggle', null, { 
        params: { enabled } 
      }),
    
    clearAIQuickQuestions: () =>
      apiClient.delete('/settings/ai-quick-questions'),
    
    generateAIQuickQuestionsFromPrompt: (prompt: string) =>
      apiClient.post('/settings/ai-quick-questions/generate-from-prompt', { prompt }, {
        timeout: 60000 // 1 minute
      }),
    
    // Tool Preferences
    getToolPreferences: () =>
      apiClient.get('/settings/tools/preferences'),
    
    updateToolPreferences: (data: { enabled_tools: Record<string, boolean> }) =>
      apiClient.put('/settings/tools/preferences', data),
    
    // Order Status Mappings
    getOrderStatuses: () =>
      apiClient.get('/settings/order-statuses'),
    
    updateOrderStatuses: (data: { paid_statuses: string[]; unpaid_statuses: string[] }) =>
      apiClient.put('/settings/order-statuses', data),

    // Report Schedules (owner/manager)
    getReportSchedules: (storeId: number) =>
      apiClient.get('/settings/report-schedules', { params: { store_id: storeId } }),

    updateReportSchedule: (reportType: string, storeId: number, data: {
      enabled: boolean;
      generate_hour: number;
      generate_min: number;
      day_of_week?: number | null;
      day_of_month?: number | null;
    }) =>
      apiClient.put(`/settings/report-schedules/${reportType}`, data, { params: { store_id: storeId } }),

    updateStoreTimezone: (storeId: number, timezone: string) =>
      apiClient.put('/settings/report-schedules/timezone', { timezone }, { params: { store_id: storeId } }),

    /** `date` (YYYY-MM-DD) reproduces a specific window; omit it for the default. */
    generateReportNow: (reportType: string, storeId: number, date?: string) =>
      apiClient.post(`/settings/report-schedules/${reportType}/generate-now`, null, {
        params: date ? { store_id: storeId, date } : { store_id: storeId },
      }),

    // Report Delivery Prefs (all members)
    getReportDeliveryPrefs: (storeId: number) =>
      apiClient.get('/settings/report-delivery-prefs', { params: { store_id: storeId } }),

    updateReportDeliveryPref: (reportType: string, storeId: number, data: {
      enabled: boolean;
      channels: string[];
      phone?: string | null;
      deliver_hour?: number;
      deliver_min?: number;
    }) =>
      apiClient.put(`/settings/report-delivery-prefs/${reportType}`, data, { params: { store_id: storeId } }),

    sendReportNow: (reportType: string, storeId: number) =>
      apiClient.post(`/settings/report-delivery-prefs/${reportType}/send-now`, null, { params: { store_id: storeId } }),

    updateReportDeliveryEmail: (storeId: number, email: string | null) =>
      apiClient.put('/settings/store-membership/report-delivery-email', { email }, { params: { store_id: storeId } }),
  },

  // Chat
  chat: {
    generatePlan: (data: { message: string; conversation_id?: string; use_cache?: boolean }) =>
      apiClient.post('/chat/plan', data),

    previewPlanPrompt: (data: PlanPromptPreviewRequest) =>
      apiClient.post<PlanPromptPreview>('/chat/plan/prompt-preview', data),

    getHistory: (
      conversation_id?: string,
      params?: { limit?: number; offset?: number }
    ) =>
      apiClient.get('/chat/history', {
        params: {
          ...(params || {}),
          ...(conversation_id ? { conversation_id } : {}),
        }
      }),

    clearHistory: (conversation_id?: string) =>
      apiClient.delete('/chat/history', {
        params: conversation_id ? { conversation_id } : {}
      }),

    deleteAllConversations: () =>
      apiClient.delete('/chat/conversations/all'),

    listConversations: (params?: { limit?: number; offset?: number }) =>
      apiClient.get('/chat/conversations', {
        params,
      }),

    generateGraphs: (data: { 
      conversation_id: string;
      message: string;
      summary_text: string;
      query_results: any[];
      max_charts?: number;
      temperature?: number;
      max_tokens?: number;
      include_prompt?: boolean;
    }) => apiClient.post('/chat/generate-graphs', data),

    gatekeeper: (data: {
      message: string;
      conversation_id?: string;
      previous_status?: string;
      turn_type?: string;
    }) => apiClient.post('/chat/gatekeeper', data),

    capabilityAssessment: (data: {
      message: string;
      conversation_id?: string;
    }) => apiClient.post('/chat/capability-assessment', data),

    fastReply: (data: {
      message: string;
      query_results: any[];
      conversation_id?: string;
    }) => apiClient.post('/chat/fast-reply', data),

    deleteMessage: (messageId: number) =>
      apiClient.delete(`/chat/history/message/${messageId}`),

    // Streaming endpoints for test harness would go here
    // Note: Test harness needs to handle SSE streams for execution/summary

    // Share conversation
    shareConversation: (conversationId: string) =>
      apiClient.post(`/chat/conversations/${conversationId}/share`),

    unshareConversation: (conversationId: string) =>
      apiClient.delete(`/chat/conversations/${conversationId}/share`),

    getShareStatus: (conversationId: string) =>
      apiClient.get(`/chat/conversations/${conversationId}/share/status`),

    newConversationId: () =>
      apiClient.post<{ conversation_id: string }>('/chat/v2/conversation'),
  },

  // Public shared conversation (no auth required)
  getSharedConversation: (conversationId: string) =>
    apiClient.get(`/chat/shared/${conversationId}`),

  // Public shared report (no auth required, sui-10). Plain `fetch`, not
  // `apiClient`: this path must never carry a stored session's token or run
  // through the 401-redirect-to-login interceptor — same reasoning as the
  // shared-conversation fetch in app/(public)/share/page.tsx.
  getSharedReport: async (shareToken: string): Promise<SharedReportPayload> => {
    const response = await fetch(getApiUrl(`boxes/shared/${encodeURIComponent(shareToken)}`));
    if (!response.ok) {
      const error = new Error(`Shared report request failed: ${response.status}`) as Error & {
        status?: number;
      };
      error.status = response.status;
      throw error;
    }
    return response.json();
  },

  // Email a report's public share link (sui-11). ONE authenticated call: the
  // backend creates/refreshes the share and mails the resulting token URL, so
  // the client never renders the report and never supplies the URL to be sent.
  emailReportShareLink: (reportId: string | number, email: string) =>
    apiClient
      .post<{ success: boolean; report_id: number; sent_to: string; share_url: string }>(
        `/boxes/reports/${reportId}/share/email`,
        { email },
      )
      .then(res => res.data),

  // Dashboard
  dashboard: {
    getGraphs: () =>
      apiClient.get('/dashboard/graphs'),
    
    getCount: () =>
      apiClient.get('/dashboard/graphs/count'),
    
    pinGraph: (data: {
      chart_config: any;
      source: {
        type: string;
        query?: string;
        tool_name?: string;
        tool_args?: any;
        time_range?: any;
      };
      data?: any[];
    }) =>
      apiClient.post('/dashboard/graphs', data),
    
    updateGraph: (graphId: string, data: { title?: string; layout?: any }) =>
      apiClient.put(`/dashboard/graphs/${graphId}`, data),
    
    deleteGraph: (id: string) =>
      apiClient.delete(`/dashboard/graphs/${id}`),
    
    refreshGraph: (id: string) =>
      apiClient.post(`/dashboard/graphs/${id}/refresh`),
  },

  // Admin
  admin: {
    // User management
    // Service status (admin dashboard)
    getServiceStatus: () =>
      apiClient.get<{
        services: Array<{
          name: string;
          display_name: string;
          status: 'ok' | 'down' | 'unknown';
          latency_ms: number | null;
          message: string;
          url: string;
        }>
      }>('/admin/status/health'),

    listUsers: () =>
      apiClient.get('/admin/users'),
    
    getPendingUsers: () =>
      apiClient.get('/admin/users/pending'),
    
    approveUser: (userId: number) =>
      apiClient.post(`/admin/users/${userId}/approve`),
    
    rejectUser: (userId: number) =>
      apiClient.post(`/admin/users/${userId}/reject`),
    
    createUser: (data: {
      username: string;
      name: string;
      email: string;
      password: string;
      role: string;
    }) =>
      apiClient.post('/admin/users/create', data),
    
    resetUserPassword: (userId: number, data: { new_password: string }) =>
      apiClient.post(`/admin/users/${userId}/reset-password`, data),
    
    updateUserRole: (userId: number, data: { role: string }) =>
      apiClient.put(`/admin/users/${userId}/role`, data),
    
    deleteUser: (userId: number, options?: { delete_stores?: boolean, delete_chat_history?: boolean, delete_store_data?: boolean }) => {
      const params = new URLSearchParams();
      if (options?.delete_stores) params.append('delete_stores', 'true');
      if (options?.delete_chat_history) params.append('delete_chat_history', 'true');
      if (options?.delete_store_data) params.append('delete_store_data', 'true');
      const queryString = params.toString();
      return apiClient.delete(`/admin/users/${userId}${queryString ? `?${queryString}` : ''}`);
    },
    
    // Connect as user (token-based)
    startConnectAs: (username: string) =>
      apiClient.post(`/admin/connect-as/start/${username}`),
    
    stopConnectAs: () =>
      apiClient.post('/admin/connect-as/stop'),
  },

  // Stores management (admin only)
  stores: {
    listAll: () =>
      apiClient.get('/stores/all'),
    
    // Admin-only store creation
    create: (data: { url: string; name: string }) =>
      apiClient.post('/stores', data),
    
    // User store creation (creates store and makes user the owner)
    createUserStore: (data: { url?: string; name?: string }) =>
      apiClient.post('/stores/create', data),
    
    checkExists: (url: string) =>
      apiClient.get('/stores/check-exists', { params: { url } }),
    
    requestAccess: (storeId: number) =>
      apiClient.post(`/stores/${storeId}/request-access`),
    
    getJoinRequests: (storeId: number) =>
      apiClient.get(`/stores/${storeId}/join-requests`),
    
    approveJoinRequest: (requestId: number) =>
      apiClient.post(`/stores/join-requests/${requestId}/approve`),
    
    rejectJoinRequest: (requestId: number) =>
      apiClient.post(`/stores/join-requests/${requestId}/reject`),
    
    update: (storeId: number, data: { name: string }) =>
      apiClient.patch(`/stores/${storeId}`, data),
    
    getMembers: (storeId: number) =>
      apiClient.get(`/stores/${storeId}/members`),
    
    addMember: (storeId: number, data: { user_id: number; role: string }) =>
      apiClient.post(`/stores/${storeId}/members`, data),
    
    updateMemberRole: (storeId: number, userId: number, data: { role: string }) =>
      apiClient.put(`/stores/${storeId}/members/${userId}`, data),
    
    removeMember: (storeId: number, userId: number) =>
      apiClient.delete(`/stores/${storeId}/members/${userId}`),
    
    delete: (storeId: number) =>
      apiClient.delete(`/stores/${storeId}`),

    switch: (storeId: number) =>
      apiClient.post(`/stores/${storeId}/switch`),
  },

  // Analytics
  analytics: {
    revenue: {
      summary: (params: { 
        start_date?: string; 
        end_date?: string; 
        interval?: string;
      }) =>
        apiClient.get('/analytics/revenue/summary', { params }),
      
      stats: (params: { 
        start_date?: string; 
        end_date?: string;
      }) =>
        apiClient.get('/analytics/revenue/stats', { params }),
    },

    orders: {
      summary: (params: { 
        start_date?: string; 
        end_date?: string; 
        interval?: string;
      }) =>
        apiClient.get('/analytics/orders/summary', { params }),
      
      stats: (params: { 
        start_date?: string; 
        end_date?: string;
      }) =>
        apiClient.get('/analytics/orders/stats', { params }),

      list: (params: {
        start_date: string;
        end_date: string;
        limit?: number;
        offset?: number;
        statuses?: string;
      }) =>
        apiClient.get('/analytics/orders/list', { params }),

      origins: (params: {
        start_date: string;
        end_date: string;
      }) =>
        apiClient.get('/analytics/orders/origins', { params }),
    },

    products: {
      top: (params: { 
        limit?: number; 
        start_date?: string; 
        end_date?: string;
        order_by?: string;
      }) =>
        apiClient.get('/analytics/products/top', { params }),
      
      leaderboard: (params: { 
        limit?: number; 
        orderby?: string; 
        order?: string;
      }) =>
        apiClient.get('/analytics/products/leaderboard', { params }),
      
      details: (productId: number | string, params: {
        start_date: string;
        end_date: string;
      }) =>
        apiClient.get(`/analytics/products/${productId}`, { params }),

      report: (productId: number | string, params?: {
        start_date?: string;
        end_date?: string;
        include_ga?: boolean;
        include_orders?: boolean;
        // Comma-separated sparse fieldset (e.g. 'ga4.acquisition'). When set it
        // is authoritative server-side: only the named sections are built and
        // include_ga/include_orders are ignored. Omit for the full report.
        fields?: string;
      }) =>
        apiClient.get(`/analytics/products/${productId}/report`, { params }),

      dailyStats: (productId: number | string, params: {
        start_date: string;
        end_date: string;
      }) =>
        apiClient.get(`/analytics/products/${productId}/daily-stats`, { params }),

      salesSummary: (productId: number | string, params?: {
        periods?: string;
      }) =>
        apiClient.get(`/analytics/products/${productId}/sales-summary`, { params }),
    },

    coupons: {
      top: (params: { 
        limit?: number; 
        start_date?: string; 
        end_date?: string;
        order_by?: string;
      }) =>
        apiClient.get('/analytics/coupons/top', { params }),
      
      leaderboard: (params: { 
        limit?: number; 
        orderby?: string; 
        order?: string;
      }) =>
        apiClient.get('/analytics/coupons/leaderboard', { params }),
      
      details: (couponCode: string, params: {
        start_date: string;
        end_date: string;
      }) =>
        apiClient.get(`/analytics/coupons/${encodeURIComponent(couponCode)}`, { params }),
      
      orders: (couponCode: string, params: {
        start_date: string;
        end_date: string;
        limit?: number;
        offset?: number;
      }) =>
        apiClient.get(`/analytics/coupons/${encodeURIComponent(couponCode)}/orders`, { params }),
    },

    customers: {
      top: (params: { 
        limit?: number; 
        orderby?: string;
      }) =>
        apiClient.get('/analytics/customers/top', { params }),
    },

    countries: {
      top: (params: { 
        limit?: number; 
        start_date?: string; 
        end_date?: string;
        order_by?: string;
      }) =>
        apiClient.get('/analytics/countries/top', { params }),
    },

    cities: {
      top: (params: { 
        limit?: number; 
        start_date?: string; 
        end_date?: string;
        order_by?: string;
        country_code?: string;
      }) =>
        apiClient.get('/analytics/cities/top', { params }),
    },

    categories: {
      top: (params: { 
        limit?: number; 
        orderby?: string;
      }) =>
        apiClient.get('/analytics/categories/top', { params }),
    },

    cache: {
      clear: () =>
        apiClient.post('/analytics/cache/clear'),
    },
  },

  // Cost data uploads (legacy - use dataSources instead)
  costData: {
    list: () => apiClient.get('/cost-data/datasets'),

    upload: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      return apiClient.post('/cost-data/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    remove: (datasetId: number) => apiClient.delete(`/cost-data/datasets/${datasetId}`),

    template: () => apiClient.get('/cost-data/template', { responseType: 'blob' }),
  },

  // Data Sources - flexible user data with SKU enrichment
  dataSources: {
    list: () => apiClient.get('/data-sources/datasets'),

    upload: (file: File, datasetType: string = 'product_costs') => {
      const formData = new FormData();
      formData.append('file', file);

      return apiClient.post(`/data-sources/upload?dataset_type=${datasetType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    remove: (datasetId: number) => apiClient.delete(`/data-sources/datasets/${datasetId}`),

    getData: (datasetType: string = 'product_costs') => 
      apiClient.get(`/data-sources/data/${datasetType}`),

    template: () => apiClient.get('/data-sources/template', { responseType: 'blob' }),

    /** Download template pre-filled with store products (SKU, prices, empty cost column) */
    templateWithProducts: () => apiClient.get('/data-sources/template/products', { responseType: 'blob' }),
  },

  // Database Migrations (Admin only)
  migrations: {
    status: () =>
      apiClient.get('/admin/migrations/status').then(res => res.data),
    
    apply: () =>
      apiClient.post('/admin/migrations/apply').then(res => res.data),
    
    check: () =>
      apiClient.post('/admin/migrations/check').then(res => res.data),
  },

  // KPIs - Performance Scan
  kpis: {
    /** List all available KPIs with metadata */
    list: () =>
      apiClient.get('/kpis').then(res => res.data),
    
    /** Get registry statistics (count by category) */
    getStats: () =>
      apiClient.get('/kpis/stats').then(res => res.data),
    
    /** Get metadata for a specific KPI */
    get: (kpiId: string) =>
      apiClient.get(`/kpis/${kpiId}`).then(res => res.data),
    
    /** Run a single KPI */
    run: (kpiId: string, params: Record<string, any> = {}) =>
      apiClient.post(`/kpis/${kpiId}/run`, { params }).then(res => res.data),
    
    /** Run multiple KPIs at once */
    runMultiple: (kpiIds: string[], params: Record<string, Record<string, any>> = {}) =>
      apiClient.post('/kpis/run-multiple', { kpi_ids: kpiIds, params }).then(res => res.data),
    
    /** Get paginated data for a KPI (for full reports) */
    getPaginatedData: (kpiId: string, page: number = 1, pageSize: number = 50, params: Record<string, any> = {}) =>
      apiClient.get(`/kpis/${kpiId}/data`, { 
        params: { page, page_size: pageSize, ...params } 
      }).then(res => res.data),
    
    /** Send performance scan report via email */
    emailReport: (data: {
      email: string;
      store_name: string;
      scan_date: string;
      summary_data: Record<string, any>;
      pdf_base64?: string;
      pdf_filename?: string;
      email_title?: string;
      report_type?: string;
    }) =>
      apiClient.post('/kpis/email-report', data).then(res => res.data),
  },
};

// ============================================================================
// STORES API
// ============================================================================

export const storesApi = {
  // Get all stores for current user
  getMyStores: () =>
    apiClient.get('/stores/my-stores').then(res => res.data),

  // Get current store details
  getCurrentStore: () =>
    apiClient.get('/stores/current').then(res => res.data),

  // Switch to a different store
  switchStore: (storeId: number) =>
    apiClient.post(`/stores/switch/${storeId}`).then(res => res.data),

  // Create a new store
  createStore: (data: { url?: string; name?: string }) =>
    apiClient.post('/stores/create', data).then(res => res.data),

  // Get members of current store
  getCurrentStoreMembers: () =>
    apiClient.get('/stores/current/members').then(res => res.data),

  // Update member role
  updateMemberRole: (userId: number, role: string) =>
    apiClient.patch(`/stores/current/members/${userId}/role`, { role }).then(res => res.data),

  // Remove member from store
  removeMember: (userId: number) =>
    apiClient.delete(`/stores/current/members/${userId}`).then(res => res.data),

  // Leave current store
  leaveStore: () =>
    apiClient.post('/stores/current/leave').then(res => res.data),

  // Delete current store (owner only)
  deleteStore: () =>
    apiClient.delete('/stores/current').then(res => res.data),

  // Get pending invites for current store
  getCurrentStoreInvites: () =>
    apiClient.get('/stores/current/invites').then(res => res.data),

  // Create invite
  createInvite: (email: string, role: string) =>
    apiClient.post('/stores/current/invites', { email, role }).then(res => res.data),

  // Get join requests for current store
  getCurrentStoreJoinRequests: () =>
    apiClient.get('/stores/current/join-requests').then(res => res.data),

  // Get summary of pending actions across all stores
  getPendingActionsSummary: () =>
    apiClient.get('/stores/pending-actions-summary').then(res => res.data),

  // Approve join request
  approveJoinRequest: (requestId: number) =>
    apiClient.post(`/stores/join-requests/${requestId}/approve`).then(res => res.data),

  // Reject join request
  rejectJoinRequest: (requestId: number) =>
    apiClient.post(`/stores/join-requests/${requestId}/reject`).then(res => res.data),

  // Revoke invite
  revokeInvite: (inviteId: number) =>
    apiClient.delete(`/stores/current/invites/${inviteId}`).then(res => res.data),

  // Get public invite info (no auth required in backend, but we still send token)
  getInviteInfo: (token: string) =>
    apiClient.get(`/stores/invites/${token}/info`).then(res => res.data),

  // Accept invite
  acceptInvite: (token: string) =>
    apiClient.post(`/stores/invites/${token}/accept`).then(res => res.data),
};

export default apiClient;
